/** Mint on the person's signed-in device. Only the returned ticket goes to a VM.
 * The caller must select the answering agent's owner, never the message author.
 */
export interface SessionBillingScope {
  sessionId: string
  agentId: string
  operation: '/v1/chat' | '/v1/tts' | '/v1/realtime/session'
  /** S6: for a run's chat call, the run asking and the message it answers.
   * The auth-worker admits one run per person and message (409 for another). */
  runId?: string
  promptId?: string
}
export interface SessionBillingTicket extends SessionBillingScope {
  token: string
  expiresAt: number
}
/** Another run of this person is answering the message: stand down, do not
 * retry. Nothing was spent. */
export class PromptTakenError extends Error {
  constructor() {
    super('Another run is answering this message')
    this.name = 'PromptTakenError'
  }
}
export async function issueSessionBillingTicket(
  authServer: string,
  scope: SessionBillingScope,
  accountToken: string,
  send: typeof fetch = fetch,
): Promise<SessionBillingTicket> {
  const url = new URL('/billing/session-tokens', authServer)
  if (
    url.protocol !== 'https:' &&
    !(
      url.protocol === 'http:' &&
      ['localhost', '127.0.0.1'].includes(url.hostname)
    )
  )
    throw new Error('Billing requires an HTTPS auth server')
  if (!accountToken) throw new Error('Sign in to use inference')
  const response = await send(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accountToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(scope),
    credentials: 'omit',
    redirect: 'error',
    cache: 'no-store',
  })
  if (response.status === 409) throw new PromptTakenError()
  if (!response.ok)
    throw new Error(`Session billing refused (${response.status})`)
  const ticket = (await response.json()) as SessionBillingTicket
  if (
    !ticket ||
    !/^msb1_[a-f0-9]{64}$/.test(ticket.token) ||
    !Number.isFinite(ticket.expiresAt) ||
    ticket.expiresAt <= Date.now() ||
    ticket.expiresAt > Date.now() + 300_000 ||
    ticket.sessionId !== scope.sessionId ||
    ticket.agentId !== scope.agentId ||
    ticket.operation !== scope.operation ||
    ticket.runId !== scope.runId ||
    ticket.promptId !== scope.promptId
  )
    throw new Error('Invalid session billing ticket')
  return ticket
}
/** RFC 0345 A4: the asker pays for the turn answering their message on Mod's
 * model. Their own signed-in device pledges when they post it; the host's
 * ticket for a run answering it then charges them. Throws if refused. */
export async function pledgeSessionPrompt(
  authServer: string,
  pledge: { sessionId: string; promptId: string },
  accountToken: string,
  send: typeof fetch = fetch,
): Promise<void> {
  const url = new URL('/billing/session-tokens/pledge', authServer)
  if (
    url.protocol !== 'https:' &&
    !(
      url.protocol === 'http:' &&
      ['localhost', '127.0.0.1'].includes(url.hostname)
    )
  )
    throw new Error('Billing requires an HTTPS auth server')
  if (!accountToken) throw new Error('Sign in to pay for your turns')
  const response = await send(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accountToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sessionId: pledge.sessionId,
      promptId: pledge.promptId,
    }),
    credentials: 'omit',
    redirect: 'error',
    cache: 'no-store',
  })
  if (!response.ok) throw new Error(`Pledge refused (${response.status})`)
}

/** RFC 0345 3e/3f: the auth-worker learns whose agent a session key is from
 * the owner's device at mint time. The key signs the statement, which proves
 * the registrant held its seed; the auth-worker checks the person against the
 * account's bound location. It never sees a Keyhive grant, so it decides who
 * pays, never what the key may reach. */
export interface AgentKeyRegistration {
  /** The key's identifier (hex): its Ed25519 verifying key. */
  agentId: string
  sessionId: string
  /** The person group (hex) that delegated it. */
  personId: string
  /** Ed25519 signature (hex) by the key over agentKeyStatement. */
  signature: string
}
export const agentKeyStatement = (
  agentId: string,
  sessionId: string,
  personId: string,
) =>
  new TextEncoder().encode(
    `mod:session-agent-key:v1\n${agentId}\n${sessionId}\n${personId}`,
  )
const toHex = (bytes: Uint8Array) =>
  Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')

/** RFC 8410 PKCS#8 for a raw Ed25519 seed. The caller zeroes the seed. */
export async function ed25519FromSeed(
  seed: Uint8Array,
  usages: KeyUsage[] = ['sign'],
  extractable = false,
): Promise<CryptoKey> {
  if (seed.length !== 32) throw new Error('Invalid session key')
  const pkcs8 = new Uint8Array(48)
  pkcs8.set([
    0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70,
    0x04, 0x22, 0x04, 0x20,
  ])
  pkcs8.set(seed, 16)
  try {
    return await crypto.subtle.importKey(
      'pkcs8',
      pkcs8,
      'Ed25519',
      extractable,
      usages,
    )
  } finally {
    pkcs8.fill(0)
  }
}

export async function signAgentKeyRegistration(
  seed: Uint8Array,
  scope: Omit<AgentKeyRegistration, 'signature'>,
): Promise<AgentKeyRegistration> {
  const key = await ed25519FromSeed(seed)
  const signature = new Uint8Array(
    await crypto.subtle.sign(
      'Ed25519',
      key,
      agentKeyStatement(scope.agentId, scope.sessionId, scope.personId),
    ),
  )
  return { ...scope, signature: toHex(signature) }
}

async function accountCall(
  authServer: string,
  path: string,
  body: unknown,
  accountToken: string,
  send: typeof fetch,
): Promise<Response> {
  const url = new URL(path, authServer)
  if (
    url.protocol !== 'https:' &&
    !(
      url.protocol === 'http:' &&
      ['localhost', '127.0.0.1'].includes(url.hostname)
    )
  )
    throw new Error('Billing requires an HTTPS auth server')
  if (!accountToken) throw new Error('Sign in to use inference')
  return send(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accountToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    credentials: 'omit',
    redirect: 'error',
    cache: 'no-store',
  })
}

/** Register a freshly minted session key to the signed-in account. Idempotent
 * for the same account; a key already registered to anyone else is refused. */
export async function registerSessionAgent(
  authServer: string,
  registration: AgentKeyRegistration,
  accountToken: string,
  send: typeof fetch = fetch,
): Promise<void> {
  const response = await accountCall(
    authServer,
    '/billing/agent-keys',
    registration,
    accountToken,
    send,
  )
  if (!response.ok)
    throw new Error(`Agent key registration refused (${response.status})`)
}

/** Stop billing a session key. Its Keyhive grants are revoked separately. */
export async function revokeSessionAgentBilling(
  authServer: string,
  agentId: string,
  accountToken: string,
  send: typeof fetch = fetch,
): Promise<void> {
  const response = await accountCall(
    authServer,
    '/billing/agent-keys/revoke',
    { agentId },
    accountToken,
    send,
  )
  if (!response.ok && response.status !== 404)
    throw new Error(`Agent key revocation refused (${response.status})`)
}

/** The session keys this process holds, by session: what a host bills as. A
 * host holds at most one agent per session, its own person's (3f). */
const held = new Map<string, string>()
export function holdSessionAgent(sessionId: string, agentId: string): void {
  held.set(sessionId, agentId)
}
export function heldSessionAgent(sessionId: string): string | undefined {
  return held.get(sessionId)
}
export function releaseSessionAgent(sessionId: string, agentId?: string): void {
  if (!agentId || held.get(sessionId) === agentId) held.delete(sessionId)
}

/** A ticket is single-use. Retry by minting a new ticket, never by reusing it. */
export function sessionBillingHeaders(
  ticket: SessionBillingTicket,
): Record<string, string> {
  if (ticket.expiresAt <= Date.now())
    throw new Error('Session billing ticket expired')
  return {
    Authorization: `Bearer ${ticket.token}`,
    'X-Mod-Session': ticket.sessionId,
    'X-Mod-Agent': ticket.agentId,
  }
}
